import type { Expr, Profile } from '../core/types';
import type { State } from '../state/store';
import { indexExpr, physValue } from '../render/style';
import { indexById } from '../render/indices';
import { maskToRings, ringArea, type Point } from './geometry';
import { cellAreaM2, isGeographic, M2_PER_ACRE, M2_PER_HA } from './area';
import { classMean, type TrainingClass } from '../render/sam';
import { samClassify } from '../render/style';

export interface Grid { w: number; h: number; vals: Float64Array }

/** Interprets an expression tree over the sampled planes, one pixel at a time. */
function evaluate(p: Profile, expr: Expr, i: number, nd: number | null): number {
  const ev = (n: Expr): number => {
    if (typeof n === 'number') return n;
    if (!Array.isArray(n)) return NaN;
    const [op, ...a] = n as [string, ...Expr[]];
    switch (op) {
      case 'band': { const x = p.planes[(ev(a[0]!) as number) - 1]![i] as number;
        return nd != null && x === nd ? NaN : x; }
      case '+': return ev(a[0]!) + ev(a[1]!);
      case '-': return ev(a[0]!) - ev(a[1]!);
      case '*': return ev(a[0]!) * ev(a[1]!);
      case '/': return ev(a[0]!) / ev(a[1]!);
      case '^': return Math.pow(ev(a[0]!), ev(a[1]!));
      case 'abs': return Math.abs(ev(a[0]!));
      case 'clamp': return Math.min(Math.max(ev(a[0]!), ev(a[1]!)), ev(a[2]!));
      // The full comparison set the shader uses. A missing operator here returns NaN,
      // which reads as false and silently classifies nothing — so this list has to
      // track render/ rather than cover only what was needed at the time.
      case '<': return (ev(a[0]!) < ev(a[1]!)) as unknown as number;
      case '<=': return (ev(a[0]!) <= ev(a[1]!)) as unknown as number;
      case '>': return (ev(a[0]!) > ev(a[1]!)) as unknown as number;
      case '>=': return (ev(a[0]!) >= ev(a[1]!)) as unknown as number;
      case '==': return (ev(a[0]!) === ev(a[1]!)) as unknown as number;
      case '!=': return (ev(a[0]!) !== ev(a[1]!)) as unknown as number;
      case 'all': return a.every((x) => ev(x)) as unknown as number;
      case 'any': return a.some((x) => ev(x)) as unknown as number;
      case 'case': {
        for (let k = 0; k + 1 < a.length; k += 2) if (ev(a[k]!)) return ev(a[k + 1]!);
        return ev(a[a.length - 1]!);
      }
      default: throw new Error(`unsupported operator in expression: ${op}`);
    }
  };
  return ev(expr);
}

/** The value being displayed, per sampled cell. NaN where nodata. */
export function currentGrid(p: Profile, s: State): Grid | null {
  if (s.mode !== 'index' && s.mode !== 'single') return null;
  const w = p.sampleSize.width, h = p.sampleSize.height, n = w * h;
  const vals = new Float64Array(n).fill(NaN);
  const nd = p.raster.nodata;
  if (s.mode === 'single') {
    const pl = p.planes[s.singleBand];
    if (!pl) return null;
    for (let i = 0; i < n; i++) {
      const r = pl[i] as number;
      if (Number.isFinite(r) && !(nd != null && r === nd)) vals[i] = physValue(p, s.singleBand, r);
    }
  } else {
    const { expr } = indexExpr(p, s);
    if (!expr) return null;
    // Outside its physical bounds an index has no signal — the denominator has passed
    // through zero. Treated as nodata rather than as a very large value.
    const b = indexById(s.indexId)?.bounds;
    for (let i = 0; i < n; i++) {
      const x = evaluate(p, expr, i, nd);
      if (!Number.isFinite(x)) continue;
      if (b && (x < b[0] || x > b[1])) continue;
      vals[i] = x;
    }
  }
  return { w, h, vals };
}

/**
 * Class index per sampled cell, or -1 where nothing matched. Distinct from
 * `currentGrid`, whose values are continuous: a classification has no min/max window
 * to threshold, so filtering it by the stretch sliders silently matches nothing.
 */
export function classGrid(p: Profile, s: State): { grid: Int16Array; w: number; h: number } | null {
  const r = samClassify(p, s);
  if (!r) return null;
  const w = p.sampleSize.width, h = p.sampleSize.height;
  const grid = new Int16Array(w * h).fill(-1);
  const nd = p.raster.nodata;
  for (let i = 0; i < grid.length; i++) {
    if (!evaluate(p, r.alpha, i, nd)) continue;
    const k = evaluate(p, r.winner, i, nd);
    if (Number.isFinite(k)) grid[i] = k;
  }
  return { grid, w, h };
}

export interface ClassArea {
  index: number; name: string; colour: [number, number, number];
  cells: number; fraction: number;
  areaM2: number | null; areaHa: number | null; areaAcres: number | null;
}

export interface ClassSummary {
  classes: ClassArea[];
  classified: number; valid: number;
  w: number; h: number;
  approximate: boolean;
}

/** Ground area per trained class — the number an agronomist is actually after. */
export function classSummary(p: Profile, s: State): ClassSummary | null {
  const g = classGrid(p, s);
  const r = samClassify(p, s);
  if (!g || !r) return null;
  const cell = cellAreaM2(p.raster, g.w, g.h);
  const counts = new Array(r.used.length).fill(0);
  let classified = 0, valid = 0;
  const nd = p.raster.nodata;
  for (let i = 0; i < g.grid.length; i++) {
    const anyBand = p.planes[0]![i] as number;
    if (!Number.isFinite(anyBand) || (nd != null && anyBand === nd)) continue;
    valid++;
    const k = g.grid[i]!;
    if (k >= 0) { counts[k]++; classified++; }
  }
  return {
    w: g.w, h: g.h, classified, valid,
    approximate: isGeographic(p.raster),
    classes: r.used.map((c, i) => {
      const m2 = cell != null ? counts[i] * cell : null;
      return {
        index: i, name: c.name, colour: c.colour, cells: counts[i],
        fraction: valid ? counts[i] / valid : 0,
        areaM2: m2, areaHa: m2 != null ? m2 / M2_PER_HA : null,
        areaAcres: m2 != null ? m2 / M2_PER_ACRE : null,
      };
    }),
  };
}

/** Matched regions per class, each polygon carrying its class name and area. */
export async function classificationByClassGeoJSON(
  p: Profile, s: State,
): Promise<Record<string, unknown> | null> {
  const g = classGrid(p, s);
  const sum = classSummary(p, s);
  if (!g || !sum) return null;
  const project = await projector(p, g.w, g.h);
  const cell = cellAreaM2(p.raster, g.w, g.h) ?? 0;
  const features: unknown[] = [];

  sum.classes.forEach((c) => {
    const mask = new Uint8Array(g.grid.length);
    for (let i = 0; i < g.grid.length; i++) if (g.grid[i] === c.index) mask[i] = 1;
    for (const comp of maskToRings(mask, g.w, g.h, 4)) {
      const rings = comp.rings.map((rg) => ({ ring: rg, area: ringArea(rg) }));
      rings.sort((a, b) => Math.abs(b.area) - Math.abs(a.area));
      features.push({
        type: 'Feature',
        properties: {
          class: c.name, class_index: c.index,
          colour: `#${c.colour.map((x) => x.toString(16).padStart(2, '0')).join('')}`,
          cells: comp.cells,
          area_m2: +(comp.cells * cell).toFixed(2),
          area_ha: +(comp.cells * cell / M2_PER_HA).toFixed(4),
          area_acres: +(comp.cells * cell / M2_PER_ACRE).toFixed(4),
        },
        geometry: { type: 'Polygon', coordinates: rings.map((x) => x.ring.map(project.fn)) },
      });
    }
  });

  return {
    type: 'FeatureCollection', features,
    properties: {
      source: p.url, generated: new Date().toISOString(),
      method: 'spectral angle mapper', maxAngleDeg: s.samMaxAngle,
      sampled: `${g.w}x${g.h} of ${p.raster.width}x${p.raster.height}`,
      classifiedFraction: sum.valid ? +(sum.classified / sum.valid).toFixed(4) : 0,
      classes: sum.classes.map((c) => ({
        name: c.name, cells: c.cells,
        area_ha: c.areaHa != null ? +c.areaHa.toFixed(4) : null,
      })),
      note: 'Traced on a downsampled overview; boundaries follow that cell grid.'
        + (project.wgs ? '' : ' Coordinates are in the source CRS.'),
      crs: project.wgs ? 'EPSG:4326' : (p.raster.epsg ? 'EPSG:' + p.raster.epsg : null),
    },
    ...(project.wgs || !p.raster.epsg
      ? {}
      : { crs: { type: 'name', properties: { name: 'EPSG:' + p.raster.epsg } } }),
  };
}

/** One row per class, for a spreadsheet. */
export function classSummaryCSV(p: Profile, s: State): string | null {
  const sum = classSummary(p, s);
  if (!sum) return null;
  const rows = [['class', 'cells', 'percent_of_scene', 'area_ha', 'area_acres'].join(',')];
  for (const c of sum.classes) {
    rows.push([JSON.stringify(c.name), c.cells, (c.fraction * 100).toFixed(3),
      c.areaHa?.toFixed(4) ?? '', c.areaAcres?.toFixed(4) ?? ''].join(','));
  }
  const un = sum.valid - sum.classified;
  rows.push([JSON.stringify('(unclassified)'), un,
    sum.valid ? (un / sum.valid * 100).toFixed(3) : '0', '', ''].join(','));
  return rows.join('\n') + '\n';
}

/**
 * The 2nd and 98th percentiles of what the current view actually contains.
 *
 * An index's declared range is theoretical — NDVI can reach -1 to 1, but a given
 * scene rarely spans more than a fraction of it, and stretching across the full range
 * wastes most of the colour ramp on values that are not present. Roughly 30 ms over
 * the sampled grid, which is cheap enough to run whenever the view changes.
 *
 * Returns null where the data is degenerate, so the caller can fall back to the
 * declared range rather than showing a flat image.
 */
export function autoIndexRange(p: Profile, s: State): [number, number] | null {
  const grid = currentGrid(p, s);
  if (!grid) return null;
  const vals: number[] = [];
  for (let i = 0; i < grid.vals.length; i++) {
    const v = grid.vals[i]!;
    if (Number.isFinite(v)) vals.push(v);
  }
  if (vals.length < 32) return null;
  vals.sort((a, b) => a - b);
  const lo = vals[Math.floor(vals.length * 0.02)]!;
  const hi = vals[Math.floor(vals.length * 0.98)]!;
  if (!(hi > lo)) return null;
  const round = (x: number) => +x.toPrecision(3);
  return [round(lo), round(hi)];
}

export interface ClassStats {
  grid: Grid; mask: Uint8Array;
  matched: number; valid: number; fraction: number;
  meanValue: number | null; p2: number | null; p98: number | null;
  areaM2: number | null; areaHa: number | null; areaAcres: number | null;
  approximate: boolean;
}

export function classificationStats(p: Profile, s: State): ClassStats | null {
  const grid = currentGrid(p, s);
  if (!grid) return null;
  const a = Math.min(s.lo, s.hi), b = Math.max(s.lo, s.hi);
  const mask = new Uint8Array(grid.w * grid.h);
  const all: number[] = [];
  let matched = 0, valid = 0, sum = 0;
  for (let i = 0; i < mask.length; i++) {
    const v = grid.vals[i]!;
    if (!Number.isFinite(v)) continue;
    valid++; all.push(v);
    if (v >= a && v <= b) { mask[i] = 1; matched++; sum += v; }
  }
  all.sort((x, y) => x - y);
  const cell = cellAreaM2(p.raster, grid.w, grid.h);
  const m2 = cell != null ? matched * cell : null;
  return {
    grid, mask, matched, valid,
    fraction: valid ? matched / valid : 0,
    meanValue: matched ? sum / matched : null,
    p2: all.length ? all[Math.floor(all.length * 0.02)]! : null,
    p98: all.length ? all[Math.floor(all.length * 0.98)]! : null,
    areaM2: m2, areaHa: m2 != null ? m2 / M2_PER_HA : null,
    areaAcres: m2 != null ? m2 / M2_PER_ACRE : null,
    approximate: isGeographic(p.raster),
  };
}

/** Matched regions as WGS84 GeoJSON, with per-feature area. */
/** Cell coordinates to WGS84 where a transform is available. */
async function projector(p: Profile, gw: number, gh: number) {
  const r = p.raster;
  const pxW = r.bbox ? (r.bbox[2]! - r.bbox[0]!) / gw : 1;
  const pxH = r.bbox ? (r.bbox[3]! - r.bbox[1]!) / gh : 1;
  const originX = r.bbox ? r.bbox[0]! : 0;
  const originY = r.bbox ? r.bbox[3]! : 0;
  const src = r.epsg ? 'EPSG:' + r.epsg : null;
  let forward: ((c: number[]) => number[]) | null = null;
  let wgs = src === 'EPSG:4326';
  if (src && !wgs) {
    try {
      const proj4 = (await import('proj4')).default;
      const f = proj4(src, 'EPSG:4326');
      forward = (c) => f.forward(c);
      wgs = true;
    } catch { forward = null; }
  }
  return {
    wgs,
    fn: ([px, py]: Point): [number, number] => {
      const X = originX + px * pxW, Y = originY - py * pxH;
      const q = forward ? forward([X, Y]) : [X, Y];
      return [+q[0]!.toFixed(8), +q[1]!.toFixed(8)];
    },
  };
}

export async function classificationGeoJSON(
  p: Profile, s: State,
): Promise<Record<string, unknown> | null> {
  const st = classificationStats(p, s);
  if (!st) return null;
  const { grid, mask } = st;
  const r = p.raster;
  const comps = maskToRings(mask, grid.w, grid.h, 4);
  const pxW = r.bbox ? (r.bbox[2]! - r.bbox[0]!) / grid.w : 1;
  const pxH = r.bbox ? (r.bbox[3]! - r.bbox[1]!) / grid.h : 1;
  const originX = r.bbox ? r.bbox[0]! : 0;
  const originY = r.bbox ? r.bbox[3]! : 0;
  const src = r.epsg ? 'EPSG:' + r.epsg : null;

  let forward: ((c: number[]) => number[]) | null = null;
  let wgs = src === 'EPSG:4326';
  if (src && !wgs) {
    try {
      const proj4 = (await import('proj4')).default;
      const f = proj4(src, 'EPSG:4326');
      forward = (c) => f.forward(c);
      wgs = true;
    } catch { forward = null; }
  }
  const project = ([px, py]: Point): [number, number] => {
    const X = originX + px * pxW, Y = originY - py * pxH;
    const q = forward ? forward([X, Y]) : [X, Y];
    return [+q[0]!.toFixed(8), +q[1]!.toFixed(8)];
  };
  const cell = cellAreaM2(r, grid.w, grid.h) ?? 0;

  const features = comps.map((c, i) => {
    const rings = c.rings.map((rg) => ({ ring: rg, area: ringArea(rg) }));
    rings.sort((a, b) => Math.abs(b.area) - Math.abs(a.area));
    return {
      type: 'Feature',
      properties: {
        id: i + 1, cells: c.cells,
        area_m2: +(c.cells * cell).toFixed(2),
        area_ha: +(c.cells * cell / M2_PER_HA).toFixed(4),
        area_acres: +(c.cells * cell / M2_PER_ACRE).toFixed(4),
      },
      geometry: { type: 'Polygon', coordinates: rings.map((x) => x.ring.map(project)) },
    };
  });

  const fc: Record<string, unknown> = {
    type: 'FeatureCollection', features,
    properties: {
      source: p.url, generated: new Date().toISOString(),
      mode: s.mode, index: s.mode === 'index' ? s.indexId : null,
      threshold: { min: Math.min(s.lo, s.hi), max: Math.max(s.lo, s.hi) },
      sampled: `${grid.w}x${grid.h} of ${r.width}x${r.height}`,
      note: 'Traced on a downsampled overview; boundaries follow that cell grid.'
        + (wgs ? '' : ' Coordinates are in the source CRS — no WGS84 transform was available.'),
      crs: wgs ? 'EPSG:4326' : src,
    },
  };
  if (!wgs && src) fc['crs'] = { type: 'name', properties: { name: src } };
  return fc;
}

/**
 * Class spectra as CSV — a reusable spectral library. Each class contributes its mean
 * plus every training point, so the file can be inspected as well as reused.
 */
export function spectraCSV(classes: TrainingClass[]): string | null {
  const rows: string[][] = [];
  const nms = [...new Set(classes.flatMap((c) =>
    c.points.flatMap((p) => p.map((x) => x.nm))))].sort((a, b) => a - b);
  if (!nms.length) return null;
  for (const c of classes) {
    const mean = classMean(c.points);
    if (mean) {
      const by = Object.fromEntries(mean.map((x) => [x.nm, x.val]));
      rows.push([JSON.stringify(c.name), 'mean',
        ...nms.map((n) => (by[n] != null ? by[n]!.toPrecision(6) : ''))]);
    }
    c.points.forEach((p, i) => {
      const by = Object.fromEntries(p.map((x) => [x.nm, x.val]));
      rows.push([JSON.stringify(c.name), `point ${i + 1}`,
        ...nms.map((n) => (by[n] != null ? by[n]!.toPrecision(6) : ''))]);
    });
  }
  if (!rows.length) return null;
  return [['class', 'kind', ...nms.map((n) => n + 'nm')].join(','),
    ...rows.map((r) => r.join(','))].join('\n') + '\n';
}
